import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import {
  BEREICH_FARBEN,
  Clip,
  Exportbereich,
  KI_UEBERGANG_ART,
  TextStyle,
  Track,
  istGespraech,
  kiClips,
  kiPositionen,
  kiUebergaengeAnClips,
  kiUntertitel,
  zeitImSchnitt,
  zoomKeyframes,
  type Op,
} from '@schnitt/shared';
import { api, type KiVorschlagView, type TranscriptSummary, type VorlageView } from '../api';
import { ausDatei, useEditor } from '../store';
import { newId } from '../ids';
import { Sheet } from './Sheet';

/**
 * **KI-Schnitt** (7.66; Sascha, 3. November: „Rohmaterial hochladen und
 * schneiden automatisch“ -- und dazu: „ist das UX gut genug um schlau mit
 * der KI zu arbeiten? Sodass ich es dann im Schnitt anpassen kann?“).
 *
 * Drei Schritte, und zwischen jedem entscheidet ein Mensch:
 *
 *  1. *Auftrag*: welche Vorlage, ein Satz Briefing. Das Material ist von
 *     selbst alles mit Ton, und was noch kein Transkript hat, wird beim
 *     „Vorschlag holen“ transkribiert (Sascha, 4. November: „warum muss ich
 *     die Medien erst auswählen … es muss alles automatischer gehen. Auch
 *     transkribieren“). Wer es enger will, klappt die Liste auf.
 *  2. *Vorschlag*: was die KI nehmen würde, als Liste mit dem gesprochenen
 *     Text -- jede Stelle abwählbar, und „nachschärfen“ („kürzer“, „anderer
 *     Einstieg“) baut auf dem auf, was jetzt dasteht.
 *  3. *Übernehmen*: Clips auf eine eigene Spur, mit dem Gesagten als Namen.
 *     Eine gewöhnliche Änderung -- rückgängig zu machen, danach von Hand
 *     und im Team weiterzuschneiden.
 */
export function KiSchnittSheet({ onClose }: { onClose: () => void }): ReactElement {
  const project = useEditor((s) => s.project);
  const assets = useEditor((s) => s.assets);
  const role = useEditor((s) => s.role);
  const dispatch = useEditor((s) => s.dispatch);
  const toast = useEditor((s) => s.toast);
  const timeline = useEditor((s) => s.timeline);

  const [vorlagen, setVorlagen] = useState<VorlageView[]>([]);
  const [abschriften, setAbschriften] = useState<TranscriptSummary[]>([]);
  /** `null`: noch nicht angefasst -- dann gilt alles mit Ton. */
  const [handverlesen, setGewaehlt] = useState<string[] | null>(null);
  const [listeOffen, setListeOffen] = useState(false);
  /** Was gerade passiert, in Worten -- „Transkribiere … 1 von 3 fertig“. */
  const [schritt, setSchritt] = useState<string | null>(null);
  const offen = useRef(true);
  useEffect(
    () => () => {
      offen.current = false;
    },
    [],
  );
  const [vorlageId, setVorlageId] = useState<string | null>(null);
  const [briefing, setBriefing] = useState('');
  const [vorschlag, setVorschlag] = useState<KiVorschlagView | null>(null);
  const [aus, setAus] = useState<Set<string>>(new Set());
  /** Abgewählte Hinweise („h0“) und Bereiche („b1“) -- 4. November. */
  const [ohne, setOhne] = useState<Set<string>>(new Set());
  /** Füllwörter und lange Pausen heraus (4. November) -- von selbst an. */
  const [straffen, setStraffen] = useState(true);
  /** Untertitel mit Schlagwörtern (4. November) -- von selbst an. */
  const [mitUntertiteln, setMitUntertiteln] = useState(true);
  const refreshKommentare = useEditor((s) => s.refreshKommentare);
  const [rueckmeldung, setRueckmeldung] = useState('');
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const mitTon = useMemo(() => assets.filter((a) => a.hasAudio && a.status === 'ready'), [assets]);
  // Von selbst nur, was spricht (4. November): B-Roll ohne Sprache bleibt
  // draussen und wird nicht transkribiert. Höchstens 20, so viele nimmt der
  // Server.
  const gespraeche = useMemo(() => mitTon.filter((a) => istGespraech(a.sprachanteil, a.artHand)), [mitTon]);
  const gewaehlt = handverlesen ?? gespraeche.slice(0, 20).map((a) => a.id);
  const brollZahl = mitTon.length - gespraeche.length;

  const laden = async () => {
    if (!project) return;
    try {
      const [v, t] = await Promise.all([api.vorlagen('ki'), api.transcripts(project.id)]);
      setVorlagen(v.vorlagen);
      setAbschriften(t.transcripts);
      setVorlageId((alt) => alt ?? v.vorlagen[0]?.id ?? null);
    } catch (err) {
      setFehler(err instanceof Error ? err.message : 'Die Vorlagen liessen sich nicht holen.');
    }
  };
  useEffect(() => {
    void laden();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id]);

  const neuesteIn = (liste: TranscriptSummary[], assetId: string) =>
    liste.filter((t) => t.asset_id === assetId).sort((a, b) => (a.created_at < b.created_at ? 1 : -1))[0] ?? null;
  const abschriftVon = (assetId: string) => neuesteIn(abschriften, assetId);

  /**
   * Sorgt dafür, dass das gewählte Material ein Transkript hat: startet, was
   * fehlt oder gescheitert ist, und wartet, bis alles durch ist. Zurück kommt,
   * was geschnitten werden kann -- was nicht ging, wird genannt und
   * übersprungen, statt den ganzen Auftrag aufzuhalten.
   */
  const transkripteSicherstellen = async (ids: string[]): Promise<string[]> => {
    if (!project) return [];
    let liste = (await api.transcripts(project.id)).transcripts;
    const fehlen = ids.filter((id) => {
      const t = neuesteIn(liste, id);
      return !t || t.status === 'failed';
    });
    for (const id of fehlen) await api.startTranscription(id, 'de');
    const frist = Date.now() + 30 * 60_000;
    for (;;) {
      liste = (await api.transcripts(project.id)).transcripts;
      if (!offen.current) return [];
      setAbschriften(liste);
      const stand = ids.map((id) => neuesteIn(liste, id)?.status ?? 'pending');
      const laufen = stand.filter((x) => x === 'pending' || x === 'running').length;
      if (laufen === 0) break;
      setSchritt(`Transkribiere … ${ids.length - laufen} von ${ids.length} fertig`);
      if (Date.now() > frist) throw new Error('Das Transkribieren dauert ungewöhnlich lange. Später noch einmal versuchen.');
      await new Promise((r) => setTimeout(r, 2500));
    }
    const fertig = ids.filter((id) => neuesteIn(liste, id)?.status === 'ready');
    const nicht = ids.filter((id) => !fertig.includes(id)).map((id) => assets.find((a) => a.id === id)?.name ?? id);
    if (fertig.length === 0) {
      const grund = neuesteIn(liste, ids[0]!)?.error;
      throw new Error(`Transkribieren ging nicht${grund ? `: ${grund.split('\n')[0]}` : '.'} Ist unter Verwaltung → KI die Transkription eingerichtet?`);
    }
    if (nicht.length) toast('info', `Ohne Transkript, daher übersprungen: ${nicht.map((n) => `„${n}“`).join(', ')}.`);
    return fertig;
  };

  const holen = async (vorher?: { abschnitte: string[]; rueckmeldung: string }) => {
    if (!project || !vorlageId || gewaehlt.length === 0) return;
    setBusy(true);
    setFehler(null);
    try {
      setSchritt('Prüfe die Transkripte …');
      const material = await transkripteSicherstellen(gewaehlt);
      if (!offen.current || material.length === 0) return;
      setSchritt(vorher ? 'Die KI schärft nach …' : 'Die KI liest das Material …');
      const { vorschlag: neu } = await api.kiSchnitt(project.id, { vorlageId, briefing, assetIds: material, vorher });
      setVorschlag(neu);
      setAus(new Set());
      setOhne(new Set());
      setRueckmeldung('');
    } catch (err) {
      setFehler(err instanceof Error ? err.message : 'Die KI hat nicht geantwortet.');
    } finally {
      setBusy(false);
      setSchritt(null);
    }
  };

  const gewaehlteStellen = vorschlag ? vorschlag.abschnitte.filter((a) => !aus.has(a.id)) : [];
  // Mit oder ohne Füllwörter und Pausen -- dieselben Stellen.
  const behalten = straffen ? gewaehlteStellen : gewaehlteStellen.map(({ teile: _t, ...a }) => a);
  // Geschnitten wird mit Luft, und was in der Datei aufeinander folgt, wird
  // ein Clip (4. November) -- also zählt auch die Länge so.
  const clips = kiClips(behalten);
  const dauer = clips.reduce((s, c) => s + (c.bis - c.von), 0);
  const dauerVoll = kiClips(gewaehlteStellen.map(({ teile: _t, ...a }) => a)).reduce((s, c) => s + (c.bis - c.von), 0);
  const gespart = Math.max(0, dauerVoll - kiClips(gewaehlteStellen).reduce((s, c) => s + (c.bis - c.von), 0));
  const clipAnfang: number[] = [];
  clips.reduce((t, c) => (clipAnfang.push(t), t + (c.bis - c.von)), 0);
  const lage = kiPositionen(clips, behalten);

  // Hinweise an Stellen, die noch dabei sind; Bereiche über die Stellen
  // zwischen ihrem ersten und letzten, die noch dabei sind (4. November).
  const reihe = vorschlag ? vorschlag.abschnitte.map((a) => a.id) : [];
  const hinweise = (vorschlag?.hinweise ?? [])
    .map((h, i) => ({ ...h, key: `h${i}`, wo: lage.get(h.bei) }))
    .filter((h): h is typeof h & { wo: { start: number; ende: number; clip: number } } => Boolean(h.wo));
  const bereiche = (vorschlag?.bereiche ?? [])
    .map((b, i) => {
      const drin = reihe.slice(reihe.indexOf(b.von), reihe.indexOf(b.bis) + 1).map((id) => lage.get(id)).filter((x) => x !== undefined);
      return {
        ...b,
        key: `b${i}`,
        start: drin.length ? Math.min(...drin.map((x) => x.start)) : 0,
        ende: drin.length ? Math.max(...drin.map((x) => x.ende)) : 0,
        leer: drin.length === 0,
      };
    })
    .filter((b) => !b.leer && b.ende > b.start);
  // Übergänge nur, wo wirklich ein Schnitt ist; Bauchbinden an Stellen, die
  // noch dabei sind (4. November).
  const anClips = kiUebergaengeAnClips(clips, vorschlag?.uebergaenge ?? []);
  const uebergaenge = [...anClips.entries()].map(([clip, u]) => ({ ...u, clip, key: `u${u.vor}`, bei: lage.get(u.vor)?.start ?? 0 }));
  const bauchbinden = (vorschlag?.bauchbinden ?? [])
    .map((b, i) => ({ ...b, key: `l${i}`, wo: lage.get(b.bei) }))
    .filter((b): b is typeof b & { wo: { start: number; ende: number; clip: number } } => Boolean(b.wo));
  // Zoom auf die sprechende Person (4. November): an Stellen, die noch dabei sind.
  const zooms = (vorschlag?.zooms ?? [])
    .map((z) => ({ ...z, key: `z${z.bei}${z.wort ?? ''}`, wo: lage.get(z.bei) }))
    .filter((z): z is typeof z & { wo: { start: number; ende: number; clip: number } } => Boolean(z.wo));
  const zoomsAn = zooms.filter((z) => !ohne.has(z.key));
  const uebergaengeAn = uebergaenge.filter((u) => !ohne.has(u.key));
  const bauchbindenAn = bauchbinden.filter((b) => !ohne.has(b.key));
  const bindeVorlage = project?.brandKit?.templates.find((t) => t.kind === 'lowerThird') ?? null;
  const hinweiseAn = hinweise.filter((h) => !ohne.has(h.key));
  const bereicheAn = bereiche.filter((b) => !ohne.has(b.key));
  const umschalten = (key: string) => {
    const neu = new Set(ohne);
    if (neu.has(key)) neu.delete(key);
    else neu.add(key);
    setOhne(neu);
  };

  // Die Untertitel des Schnitts: Wörter aller Stellen, Schlagwörter aus der
  // Antwort und aus den Schlagwort-Zooms.
  const untertitel = (() => {
    if (!vorschlag) return null;
    const worte: Record<string, { start: number; end: number; text: string }[]> = {};
    for (const a of behalten) if (a.worte?.length) (worte[a.medium] ??= []).push(...a.worte);
    if (Object.keys(worte).length === 0) return null;
    for (const liste of Object.values(worte)) liste.sort((x, y) => x.start - y.start);
    const medienVon = new Map(vorschlag.abschnitte.map((a) => [a.id, a.medium]));
    const betont = [
      ...(vorschlag.schlagwoerter ?? []).filter((w) => w.von !== undefined && w.bis !== undefined).map((w) => ({ von: w.von!, bis: w.bis!, medium: medienVon.get(w.bei) ?? '' })),
      ...(vorschlag.zooms ?? []).filter((z) => z.wortVon !== undefined && z.wortBis !== undefined).map((z) => ({ von: z.wortVon!, bis: z.wortBis!, medium: medienVon.get(z.bei) ?? '' })),
    ];
    const t = kiUntertitel(clips, worte, betont);
    return t.segments.length ? t : null;
  })();
  const betonteZahl = untertitel ? untertitel.segments.reduce((n, seg) => n + (seg.betont?.length ?? 0), 0) : 0;

  const uebernehmen = async () => {
    if (!vorschlag || behalten.length === 0) return;
    const kinds = new Set(behalten.map((a) => assets.find((x) => x.id === a.medium)?.kind));
    const art: Track['kind'] = kinds.has('video') ? 'video' : 'audio';
    let n = 1;
    while (timeline.tracks.some((t) => t.id === `KI${n}`)) n++;
    const spur = `KI${n}`;
    const ops: Op[] = [{ op: 'addTrack', track: Track.parse({ id: spur, kind: art, name: `KI-Rohschnitt ${n > 1 ? n : ''}`.trim() }) } as Op];
    let t = 0;
    const clipIds: string[] = [];
    const weich = new Map(uebergaengeAn.map((u) => [u.clip, u]));
    let clipStart = 0;
    for (const [index, c] of clips.entries()) {
      const laenge = c.bis - c.von;
      // Die Zooms dieses Clips als Keyframes, Zeiten im Clip.
      const keyframes = zoomsAn
        .filter((z) => z.wo.clip === index)
        .flatMap((z) => {
          // Ein Schlagwort: genau auf das Wort, und noch einen Augenblick
          // darüber hinaus -- höchstens bis zum Ende der Stelle.
          if (z.wortVon !== undefined && z.wortBis !== undefined) {
            const medium = behalten.find((a) => a.id === z.bei)!.medium;
            const anfang = zeitImSchnitt(clips, medium, z.wortVon);
            const ende = zeitImSchnitt(clips, medium, z.wortBis);
            if (anfang && anfang.clip === index) {
              const bis = ende && ende.clip === index ? ende.zeit : clipAnfang[index]! + laenge;
              return zoomKeyframes(z, anfang.zeit - clipStart, Math.min(z.wo.ende - clipStart, bis - clipStart + 0.8), laenge);
            }
            return [];
          }
          return zoomKeyframes(z, z.wo.start - clipStart, z.wo.ende - clipStart, laenge);
        })
        .sort((p, q) => p.at - q.at)
        .slice(0, 32);
      clipStart += laenge;
      const clipId = newId('c');
      clipIds.push(clipId);
      ops.push(
        ausDatei({
          op: 'addClip',
          trackId: spur,
          clip: Clip.parse({
            id: clipId,
            source: { kind: 'asset', assetId: c.medium },
            start: t,
            duration: laenge,
            sourceIn: c.von,
            label: c.text.slice(0, 80),
            transitionIn: weich.has(index) ? { kind: KI_UEBERGANG_ART[weich.get(index)!.art], duration: Math.min(0.5, laenge / 3) } : null,
            keyframes,
          }),
        } as Op),
      );
      t += laenge;
    }
    // Bauchbinden auf einer eigenen Ebene über der KI-Spur: mit der Vorlage
    // des Markensets, wenn es eine gibt, sonst als schlichter Text.
    if (bauchbindenAn.length) {
      const ebene = `${spur}-B`;
      ops.push({ op: 'addTrack', track: Track.parse({ id: ebene, kind: 'video', name: `Bauchbinden ${spur}` }) } as Op);
      for (const b of bauchbindenAn) {
        const dauerB = Math.max(1, Math.min(4, t - b.wo.start));
        const slots = (bindeVorlage?.layers ?? [])
          .filter((l): l is Extract<typeof l, { type: 'text' }> => l.type === 'text')
          .map((l) => l.slot);
        const source = bindeVorlage
          ? { kind: 'brand' as const, templateId: bindeVorlage.id, fields: Object.fromEntries(slots.map((slot, i) => [slot, i === 0 ? b.name : i === 1 ? b.rolle : ''])) }
          : {
              kind: 'text' as const,
              text: b.rolle ? `${b.name}\n${b.rolle}` : b.name,
              style: TextStyle.parse({ size: 0.04, align: 'left', boxColor: '#000000', boxOpacity: 0.55, bold: true }),
            };
        ops.push({
          op: 'addClip',
          trackId: ebene,
          clip: Clip.parse({
            id: newId('t'),
            source,
            start: b.wo.start,
            duration: dauerB,
            fadeIn: 0.3,
            fadeOut: 0.3,
            transform: bindeVorlage ? undefined : { x: 0.32, y: 0.8, scale: 1, rotate: 0, opacity: 1 },
            label: `Bauchbinde ${b.name}`,
          }),
        } as Op);
      }
    }
    // Die Bereiche gehören zum selben Schritt: ein Rückgängig nimmt Spur und
    // Bereiche zusammen zurück.
    const belegt = (timeline.exportbereiche ?? []).length;
    bereicheAn.forEach((b, i) => {
      ops.push({
        op: 'addExportbereich',
        bereich: Exportbereich.parse({
          id: newId('b'),
          name: b.name,
          von: b.start,
          bis: b.ende,
          farbe: BEREICH_FARBEN[(belegt + i) % BEREICH_FARBEN.length],
          art: b.art,
        }),
      } as Op);
    });
    // Erst die Abschrift anlegen, dann alles in einem Schritt -- so nimmt ein
    // Rückgängig auch das Einschalten der Untertitel zurück.
    if (project && mitUntertiteln && untertitel) {
      try {
        const { transcriptId } = await api.transcriptAnlegen(project.id, `Untertitel ${spur}`, untertitel);
        ops.push({ op: 'updateSubtitles', patch: { enabled: true, transcriptId } } as Op);
      } catch (err) {
        toast('error', err instanceof Error ? err.message : 'Die Untertitel ließen sich nicht anlegen.');
      }
    }
    dispatch(ops);
    // Hinweise sind Kommentare wie die aus dem Team -- am Clip, zur Zeit der
    // Stelle. Sie gehen nach dem Schnitt hinaus, damit der Clip schon da ist.
    if (project && hinweiseAn.length) {
      void Promise.all(
        hinweiseAn.map((h) => api.kommentarSchreiben(project.id, { at: h.wo.start, text: `KI: ${h.text}`, clipId: clipIds[h.wo.clip] ?? null })),
      )
        .then(() => refreshKommentare())
        .catch((err: unknown) => toast('error', err instanceof Error ? err.message : 'Die Hinweise ließen sich nicht anlegen.'));
    }
    const dazu = [
      mitUntertiteln && untertitel ? `Untertitel${betonteZahl ? ` mit ${betonteZahl} Schlagwörtern` : ''}` : '',
      zoomsAn.length ? `${zoomsAn.length} ${zoomsAn.length === 1 ? 'Zoom' : 'Zooms'}` : '',
      uebergaengeAn.length ? `${uebergaengeAn.length} ${uebergaengeAn.length === 1 ? 'Übergang' : 'Übergänge'}` : '',
      bauchbindenAn.length ? `${bauchbindenAn.length} ${bauchbindenAn.length === 1 ? 'Bauchbinde' : 'Bauchbinden'}` : '',
      bereicheAn.length ? `${bereicheAn.length} ${bereicheAn.length === 1 ? 'Bereich' : 'Bereiche'}` : '',
      hinweiseAn.length ? `${hinweiseAn.length} ${hinweiseAn.length === 1 ? 'Hinweis' : 'Hinweise'}` : '',
    ].filter(Boolean);
    toast(
      'success',
      `KI-Rohschnitt übernommen: ${behalten.length} Stellen in ${clips.length} Clips, ${Math.round(dauer)} s, auf eigener Spur` +
        (dazu.length ? `, dazu ${dazu.join(', ')}.` : '.'),
    );
    onClose();
  };

  const darf = role !== 'viewer';
  const vorlage = vorlagen.find((v) => v.id === vorlageId) ?? null;

  return (
    <Sheet title="KI-Schnitt" onClose={onClose}>
      {fehler ? (
        <p className="error-text" data-ki-fehler>
          {fehler}
        </p>
      ) : null}

      {!vorschlag ? (
        <>
          <h3 className="ki-titel">Material</h3>
          {mitTon.length === 0 ? <p className="hint">Noch kein Medium mit Ton im Projekt.</p> : null}
          {mitTon.length > 0 ? (
            <div className="ki-material-kurz" data-ki-material-kurz>
              <span>
                {handverlesen === null
                  ? `Alle Gespräche (${gewaehlt.length})${brollZahl ? ` · ${brollZahl} B-Roll ohne Sprache bleibt draussen` : ''}`
                  : `${gewaehlt.length} von ${mitTon.length} ausgewählt`}
                {(() => {
                  const ohne = gewaehlt.filter((id) => abschriftVon(id)?.status !== 'ready').length;
                  return ohne ? ` · ${ohne} ${ohne === 1 ? 'wird' : 'werden'} zuerst transkribiert` : ' · Transkripte da';
                })()}
              </span>
              <button className="small ghost" data-ki-material-aendern onClick={() => setListeOffen(!listeOffen)}>
                {listeOffen ? 'Fertig' : 'Ändern'}
              </button>
            </div>
          ) : null}
          {listeOffen ? (
          <>
          <div className="chips" style={{ margin: '0 0 8px' }}>
            <button className="chip" data-ki-gespraeche onClick={() => setGewaehlt(null)}>
              Nur Gespräche
            </button>
            <button className="chip" data-ki-alle onClick={() => setGewaehlt(mitTon.slice(0, 20).map((a) => a.id))}>
              Alle
            </button>
            <button className="chip" data-ki-keine onClick={() => setGewaehlt([])}>
              Keine
            </button>
          </div>
          <div className="list" data-ki-material={String(mitTon.length)}>
            {mitTon.map((a) => {
              const t = abschriftVon(a.id);
              const an = gewaehlt.includes(a.id);
              return (
                <label className="row" key={a.id} data-ki-medium={a.id}>
                  <input
                    type="checkbox"
                    checked={an}
                    disabled={!darf}
                    onChange={() => setGewaehlt(an ? gewaehlt.filter((x) => x !== a.id) : [...gewaehlt, a.id])}
                    style={{ width: 22, height: 22, minHeight: 0 }}
                  />
                  <div className="grow">
                    <div className="title">{a.name}</div>
                    <div className="sub">
                      {Math.round(a.durationSec)} s ·{' '}
                      {istGespraech(a.sprachanteil, a.artHand) ? '' : 'B-Roll · '}
                      {t?.status === 'ready' ? 'Transkript da' : t && (t.status === 'pending' || t.status === 'running') ? 'Transkript läuft…' : 'noch kein Transkript'}
                    </div>
                  </div>
                  {!t || t.status === 'failed' ? (
                    <button
                      className="small"
                      disabled={!darf || busy}
                      data-ki-transkribieren={a.id}
                      onClick={(e) => {
                        e.preventDefault();
                        void api
                          .startTranscription(a.id, 'de')
                          .then(() => laden())
                          .catch((err: unknown) => setFehler(err instanceof Error ? err.message : 'Das Transkribieren ging nicht los.'));
                      }}
                    >
                      Transkribieren
                    </button>
                  ) : null}
                </label>
              );
            })}
          </div>
          </>
          ) : null}

          <h3 className="ki-titel">Vorlage</h3>
          {vorlagen.length === 0 ? <p className="hint">Keine KI-Vorlagen. Ein Admin legt sie unter Verwaltung → KI an.</p> : null}
          <div className="chips" style={{ marginBottom: 8 }}>
            {vorlagen.map((v) => (
              <button
                key={v.id}
                className={`chip ${vorlageId === v.id ? 'active' : ''}`}
                data-ki-vorlage-wahl={v.name}
                onClick={() => setVorlageId(v.id)}
              >
                {v.name}
              </button>
            ))}
          </div>
          {vorlage ? <p className="hint">{vorlage.satz}</p> : null}

          <label className="field">
            <span>Briefing (optional)</span>
            <textarea
              rows={2}
              maxLength={2000}
              value={briefing}
              data-ki-briefing
              onChange={(e) => setBriefing(e.target.value)}
              placeholder="z. B. Für Instagram, Thema Laternenumzug, Moderator vorne"
            />
          </label>
          <button
            className="primary"
            style={{ width: '100%' }}
            disabled={!darf || busy || gewaehlt.length === 0 || !vorlageId}
            data-ki-holen
            onClick={() => void holen()}
          >
            {busy ? (schritt ?? 'Einen Moment …') : 'Vorschlag holen'}
          </button>
        </>
      ) : (
        <>
          <div className="card" style={{ margin: '0 0 12px' }} data-ki-vorschlag={String(vorschlag.abschnitte.length)}>
            <div className="title">
              {behalten.length} von {vorschlag.gesamt} Stellen · etwa {Math.round(dauer)} s
              {vorschlag.zielSekunden ? ` (Ziel ${Math.round(vorschlag.zielSekunden)} s)` : ''}
            </div>
            {vorschlag.begruendung ? <div className="sub">{vorschlag.begruendung}</div> : null}
            {vorschlag.bilder ? (
              <div className="sub" data-ki-bilder={String(vorschlag.bilder)}>
                {vorschlag.bilder} {vorschlag.bilder === 1 ? 'Standbild' : 'Standbilder'} angesehen
              </div>
            ) : null}
            {vorschlag.verworfen.length ? (
              <div className="sub" title={vorschlag.verworfen.join('\n')}>
                Verworfen oder angemerkt: {vorschlag.verworfen.length}
                {vorschlag.verworfen[0]?.startsWith('Bilder:') ? ` · ${vorschlag.verworfen[0]}` : ''}
              </div>
            ) : null}
          </div>
          {untertitel ? (
            <label className="row" data-ki-untertitel={mitUntertiteln ? 'an' : 'aus'} style={{ marginBottom: 8 }}>
              <input type="checkbox" checked={mitUntertiteln} onChange={() => setMitUntertiteln(!mitUntertiteln)} style={{ width: 22, height: 22, minHeight: 0 }} />
              <div className="grow">
                <div className="title">Untertitel mit Schlagwörtern</div>
                <div className="sub">
                  {untertitel.segments.length} Zeilen{betonteZahl ? ` · ${betonteZahl} Schlagwörter hervorgehoben` : ''} · ersetzt die Untertitel des Projekts
                </div>
              </div>
            </label>
          ) : null}
          {gespart > 0.05 ? (
            <label className="row" data-ki-straffen={straffen ? 'an' : 'aus'} style={{ marginBottom: 8 }}>
              <input type="checkbox" checked={straffen} onChange={() => setStraffen(!straffen)} style={{ width: 22, height: 22, minHeight: 0 }} />
              <div className="grow">
                <div className="title">Füllwörter & Pausen raus</div>
                <div className="sub">„äh“, „ähm“ und Pausen über 0,5 s · spart {gespart.toFixed(1)} s</div>
              </div>
            </label>
          ) : null}
          <div className="list" data-ki-stellen>
            {vorschlag.abschnitte.map((a) => (
              <label className="row" key={a.id} data-ki-stelle={a.id}>
                <input
                  type="checkbox"
                  checked={!aus.has(a.id)}
                  onChange={() => {
                    const neu = new Set(aus);
                    if (neu.has(a.id)) neu.delete(a.id);
                    else neu.add(a.id);
                    setAus(neu);
                  }}
                  style={{ width: 22, height: 22, minHeight: 0 }}
                />
                <div className="grow">
                  <div className="title" style={{ whiteSpace: 'normal' }}>
                    {a.text}
                  </div>
                  <div className="sub">
                    {a.name} · {a.von.toFixed(1)}–{a.bis.toFixed(1)} s{a.sprecher ? ` · ${a.sprecher}` : ''}
                  </div>
                </div>
              </label>
            ))}
          </div>

          {uebergaenge.length || bauchbinden.length || zooms.length ? (
            <>
              <h3 className="ki-titel">Effekte</h3>
              <div className="list" data-ki-effekte={String(uebergaenge.length + bauchbinden.length + zooms.length)}>
                {zooms.map((z) => (
                  <label className="row" key={z.key} data-ki-zoom={z.bei}>
                    <input type="checkbox" checked={!ohne.has(z.key)} onChange={() => umschalten(z.key)} style={{ width: 22, height: 22, minHeight: 0 }} />
                    <div className="grow">
                      <div className="title">
                        {z.wort ? `Zoom aufs Wort „${z.wort}“` : z.art === 'sanft' ? 'Langsam heranfahren' : 'Näher springen'} · {Math.round((z.staerke - 1) * 100)} % näher
                      </div>
                      <div className="sub">
                        auf die sprechende Person, {z.wo.start.toFixed(1)}–{z.wo.ende.toFixed(1)} s im Schnitt
                      </div>
                    </div>
                  </label>
                ))}
                {uebergaenge.map((u) => (
                  <label className="row" key={u.key} data-ki-uebergang={u.vor}>
                    <input type="checkbox" checked={!ohne.has(u.key)} onChange={() => umschalten(u.key)} style={{ width: 22, height: 22, minHeight: 0 }} />
                    <div className="grow">
                      <div className="title">{{ blende: 'Weiche Blende', schwarz: 'Über Schwarz', weiss: 'Über Weiss' }[u.art]}</div>
                      <div className="sub">bei {u.bei.toFixed(1)} s im Schnitt</div>
                    </div>
                  </label>
                ))}
                {bauchbinden.map((b) => (
                  <label className="row" key={b.key} data-ki-bauchbinde={b.name}>
                    <input type="checkbox" checked={!ohne.has(b.key)} onChange={() => umschalten(b.key)} style={{ width: 22, height: 22, minHeight: 0 }} />
                    <div className="grow">
                      <div className="title">
                        Bauchbinde: {b.name}
                        {b.rolle ? ` · ${b.rolle}` : ''}
                      </div>
                      <div className="sub">
                        bei {b.wo.start.toFixed(1)} s im Schnitt · {bindeVorlage ? `Vorlage „${bindeVorlage.name}“` : 'als Text (kein Markenset)'}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </>
          ) : null}
          {bereiche.length ? (
            <>
              <h3 className="ki-titel">Bereiche</h3>
              <div className="list" data-ki-bereiche={String(bereiche.length)}>
                {bereiche.map((b) => (
                  <label className="row" key={b.key} data-ki-bereich={b.name}>
                    <input type="checkbox" checked={!ohne.has(b.key)} onChange={() => umschalten(b.key)} style={{ width: 22, height: 22, minHeight: 0 }} />
                    <div className="grow">
                      <div className="title">{b.name}</div>
                      <div className="sub">
                        {b.start.toFixed(1)}–{b.ende.toFixed(1)} s im Schnitt · {Math.round(b.ende - b.start)} s{b.art ? ` · ${b.art}` : ''}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </>
          ) : null}
          {hinweise.length ? (
            <>
              <h3 className="ki-titel">Hinweise</h3>
              <div className="list" data-ki-hinweise={String(hinweise.length)}>
                {hinweise.map((h) => (
                  <label className="row" key={h.key} data-ki-hinweis={h.bei}>
                    <input type="checkbox" checked={!ohne.has(h.key)} onChange={() => umschalten(h.key)} style={{ width: 22, height: 22, minHeight: 0 }} />
                    <div className="grow">
                      <div className="title" style={{ whiteSpace: 'normal' }}>
                        {h.text}
                      </div>
                      <div className="sub">bei {h.wo.start.toFixed(1)} s im Schnitt · wird ein Kommentar</div>
                    </div>
                  </label>
                ))}
              </div>
            </>
          ) : null}

          <label className="field" style={{ marginTop: 12 }}>
            <span>Nachschärfen</span>
            <input
              value={rueckmeldung}
              data-ki-rueckmeldung
              onChange={(e) => setRueckmeldung(e.target.value)}
              placeholder="z. B. kürzer, anderer Einstieg, ohne den Teil über …"
            />
          </label>
          <div className="stack">
            <button
              disabled={!darf || busy}
              data-ki-nachschaerfen
              onClick={() => void holen({ abschnitte: behalten.map((a) => a.id), rueckmeldung })}
            >
              {busy ? (schritt ?? 'Die KI schärft nach …') : 'Nachschärfen'}
            </button>
            <button className="primary" disabled={!darf || busy || behalten.length === 0} data-ki-uebernehmen onClick={() => void uebernehmen()}>
              Übernehmen ({behalten.length} Stellen auf eigene Spur)
            </button>
            <button className="ghost" disabled={busy} onClick={() => setVorschlag(null)}>
              Zurück zum Auftrag
            </button>
          </div>
        </>
      )}
    </Sheet>
  );
}
